import axios, { normalizeApiError } from './httpClient';
import API_BASE_URL from './apiBaseUrl';

const API = API_BASE_URL;
const SEARCH_PARAM_KEYS = new Set(['search', 'query', 'q', 'term']);
const MAX_SEARCH_LENGTH = 64;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const sanitizeQueryParams = (params = {}) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return acc;
    }

    if (typeof value === 'string') {
      const normalized = SEARCH_PARAM_KEYS.has(key.toLowerCase())
        ? value.trim().slice(0, MAX_SEARCH_LENGTH)
        : value.trim();

      if (normalized !== '') {
        acc[key] = normalized;
      }
      return acc;
    }

    acc[key] = value;
    return acc;
  }, {});

const toQueryString = (params = {}) =>
  new URLSearchParams(sanitizeQueryParams(params)).toString();

export const getApiError = (error) => {
  if (error?.uiMessage && Object.prototype.hasOwnProperty.call(error, 'status')) {
    return error;
  }
  return normalizeApiError(error);
};

export const apiErrorToToastMessage = (error, fallback = 'Request failed') => {
  const normalizedError = getApiError(error);
  return normalizedError.uiMessage || normalizedError.detail || fallback;
};

// Plans
export const getPlans = () => axios.get(`${API}/plans`);
export const getPlan = (planId) => axios.get(`${API}/plans/${planId}`);

// Packages
export const getPackages = () => axios.get(`${API}/packages`);
export const getPackage = (packageId) => axios.get(`${API}/packages/${packageId}`);

// Subscriptions
export const getCurrentSubscription = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/subscriptions/current${query ? `?${query}` : ''}`, { headers: getHeaders() });
};
export const cancelSubscription = () => 
  axios.post(`${API}/subscriptions/cancel`, {}, { headers: getHeaders() });

// Usage
export const getBalance = () => 
  axios.get(`${API}/usage/balance`, { headers: getHeaders() });
export const getUsageHistory = (limit = 30) => 
  axios.get(`${API}/usage/history?limit=${limit}`, { headers: getHeaders() });

// Calls
export const getCalls = (limitOrParams = 50, skip = 0, extraParams = {}) => {
  const defaultParams = {
    limit: typeof limitOrParams === 'number' ? limitOrParams : 50,
    skip: typeof limitOrParams === 'number' ? skip : 0,
  };
  const params = typeof limitOrParams === 'object'
    ? limitOrParams
    : { ...defaultParams, ...extraParams };
  const query = toQueryString(params);
  return axios.get(`${API}/calls${query ? `?${query}` : ''}`, { headers: getHeaders() });
};
export const getCallDetail = (callId) => 
  axios.get(`${API}/calls/${callId}`, { headers: getHeaders() });

export const patchCall = (callId, data) =>
  axios.patch(`${API}/calls/${encodeURIComponent(callId)}`, data, { headers: getHeaders() });

// Message interactions (SMS / messaging sessions)
export const getMessages = (limitOrParams = 50, skip = 0, extraParams = {}) => {
  const defaultParams = {
    limit: typeof limitOrParams === 'number' ? limitOrParams : 50,
    skip: typeof limitOrParams === 'number' ? skip : 0,
  };
  const params = typeof limitOrParams === 'object'
    ? limitOrParams
    : { ...defaultParams, ...extraParams };
  const query = toQueryString(params);
  return axios.get(`${API}/messages${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getMessageDetail = (messageId, requestConfig = {}) =>
  axios.get(`${API}/messages/${encodeURIComponent(messageId)}`, {
    ...requestConfig,
    headers: { ...getHeaders(), ...(requestConfig.headers || {}) },
  });

export const patchMessage = (messageId, data) =>
  axios.patch(`${API}/messages/${encodeURIComponent(messageId)}`, data, { headers: getHeaders() });

export const getCustomerAgents = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/agents${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getCustomerAgent = (agentId) =>
  axios.get(`${API}/agents/${agentId}`, { headers: getHeaders() });

/**
 * Customer: enable/disable Facebook messaging for an agent.
 *
 * Backend contract:
 * - Route: PATCH /api/agents/:agentId/facebook-enabled
 * - Body: { facebook_enabled: boolean } (non-boolean should return 400)
 * - Auth: cookie session (withCredentials) or Bearer token
 * - CSRF: cookie-auth PATCH requires X-CSRF-Token header; Bearer clients do not need CSRF header
 * - Expected statuses: 200, 400, 401, 403, 404, 429
 */
export const patchAgentFacebookEnabled = (agentId, data, config = {}) =>
  axios.patch(`${API}/agents/${encodeURIComponent(agentId)}/facebook-enabled`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

/** Stripe billing portal session responses may use any of these field names. */
export const pickStripeBillingPortalUrl = (data) => {
  if (!data || typeof data !== 'object') return null;
  const url = data.url || data.portal_url || data.billing_portal_url || data.billingPortalUrl;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
};

/**
 * Customer: Stripe Customer Billing Portal for this agent’s subscription (cancel, payment method, invoices).
 * Mirrors admin `POST /admin/agents/:id/subscription/stripe-billing-portal` but requires customer auth and agent ownership.
 *
 * Optional body: `return_url`, `send_email` (use `false` when the logged-in user opens the link themselves).
 */
export const createCustomerAgentStripeBillingPortalSession = (agentId, body = {}, config = {}) =>
  axios.post(
    `${API}/agents/${encodeURIComponent(agentId)}/subscription/stripe-billing-portal`,
    body,
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

// Payments
/**
 * Start Stripe Checkout.
 *
 * **success_url (backend):** Redirect to app payment success with both Stripe session id and internal order id, e.g.
 * `{originUrl}/payment/success?session_id={CHECKOUT_SESSION_ID}&order_id={YOUR_ORDER_ID}`.
 * Including `order_id` keeps compatibility with `PaymentSuccessPage`: if `session_id` is missing or still a placeholder,
 * the UI loads `GET /payments/orders/:orderId` to resolve the real Checkout Session id, then polls status (below).
 *
 * For subscription and minute package checkouts, pass `agentId` so the stored transaction has `agent_id` and
 * fulfillment can attach credits to the correct agent (packages require an active subscription).
 */
export const createCheckout = ({ itemType, itemId, agentId = null, originUrl = window.location.origin }) =>
  axios.post(
    `${API}/payments/checkout`,
    {
      item_type: itemType,
      item_id: itemId,
      ...(agentId ? { agent_id: agentId } : {}),
      origin_url: originUrl,
    },
    { headers: getHeaders() }
  );

/**
 * Calculator-based subscription checkout. Backend creates Stripe Product/Price (or equivalent), opens Checkout Session,
 * and returns `checkout_url` (and should set success_url with `session_id` + `order_id` per payments-checkout-success-flow.md).
 */
export const createDynamicSubscriptionCheckout = ({
  agentId,
  calls,
  sms,
  facebookInteractions,
  industryId = null,
  billingInterval = 'month',
  originUrl = window.location.origin,
}) =>
  axios.post(
    `${API}/payments/checkout/dynamic-subscription`,
    {
      agent_id: agentId,
      calls,
      sms,
      facebook_interactions: facebookInteractions,
      billing_interval: billingInterval,
      origin_url: originUrl,
      ...(industryId ? { industry_id: industryId } : {}),
    },
    { headers: getHeaders() }
  );

/**
 * Poll payment + fulfillment state for a Stripe Checkout Session.
 * **Route:** `GET {API}/payments/status/:sessionId` (e.g. `/api/payments/status/cs_…` when API base is `/api`).
 * The success page calls this after redirect until `payment_status` is `paid` (or timeout). Dynamic / calculator
 * checkouts should use the same redirect and polling flow — no change required on the frontend if `session_id`
 * and `order_id` are present on success_url.
 */
export const getPaymentStatus = (sessionId) =>
  axios.get(`${API}/payments/status/${encodeURIComponent(sessionId)}`, { headers: getHeaders() });

export const getPaymentOrder = (orderId) =>
  axios.get(`${API}/payments/orders/${orderId}`, { headers: getHeaders() });

export const getPaymentTransactions = () =>
  axios.get(`${API}/payments/transactions`, { headers: getHeaders() });

// Seed data
export const seedData = () => axios.post(`${API}/seed`);

// Admin APIs
export const adminGetDashboard = () => 
  axios.get(`${API}/admin/dashboard`, { headers: getHeaders() });

export const adminGetCustomers = (limit = 50, skip = 0) => 
  axios.get(`${API}/admin/customers?limit=${limit}&skip=${skip}`, { headers: getHeaders() });

export const adminGetCustomer = (userId) => 
  axios.get(`${API}/admin/customers/${userId}`, { headers: getHeaders() });

export const adminGetCallAnalytics = (params = 30) => {
  const queryParams = typeof params === 'number' ? { days: params } : (params || {});
  const query = toQueryString(queryParams);

  return axios.get(`${API}/admin/analytics/calls${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const adminGetRevenueAnalytics = (days = 30) => 
  axios.get(`${API}/admin/analytics/revenue?days=${days}`, { headers: getHeaders() });

export const adminGetPlans = () => 
  axios.get(`${API}/admin/plans`, { headers: getHeaders() });

export const adminCreatePlan = (data) => 
  axios.post(`${API}/admin/plans`, data, { headers: getHeaders() });

export const adminUpdatePlan = (planId, data) => 
  axios.put(`${API}/admin/plans/${planId}`, data, { headers: getHeaders() });

export const adminDeletePlan = (planId) => 
  axios.delete(`${API}/admin/plans/${planId}`, { headers: getHeaders() });

/**
 * Admin minute / messaging packages (add-on packs sold via checkout).
 *
 * Endpoints (relative to API base):
 * - GET    /admin/packages
 * - POST   /admin/packages
 * - PUT    /admin/packages/:packageId
 * - DELETE /admin/packages/:packageId
 *
 * Request body (POST/PUT) — backend should accept and persist:
 * - name: string
 * - minutes: number — voice minutes included in the pack
 * - message_count: number — SMS / messaging units included in the pack
 * - price: number — price in major currency units (e.g. USD), same as today
 *
 * Response list item should include the same fields plus e.g. package_id, price_per_minute, is_active.
 */
export const adminGetPackages = () =>
  axios.get(`${API}/admin/packages`, { headers: getHeaders() });

export const adminCreatePackage = (data) =>
  axios.post(`${API}/admin/packages`, data, { headers: getHeaders() });

export const adminUpdatePackage = (packageId, data) =>
  axios.put(`${API}/admin/packages/${packageId}`, data, { headers: getHeaders() });

export const adminDeletePackage = (packageId) =>
  axios.delete(`${API}/admin/packages/${packageId}`, { headers: getHeaders() });

// Admin Customer CRUD
export const adminCreateCustomer = (data) => 
  axios.post(`${API}/admin/customers`, data, { headers: getHeaders() });

export const adminUpdateCustomer = (userId, data) => 
  axios.put(`${API}/admin/customers/${userId}`, data, { headers: getHeaders() });

export const adminDeleteCustomer = (userId) => 
  axios.delete(`${API}/admin/customers/${userId}`, { headers: getHeaders() });

export const adminAddPackageToCustomer = (userId, data) => 
  axios.post(`${API}/admin/customers/${userId}/packages`, data, { headers: getHeaders() });

export const adminResendCustomerOnboardingInvite = (userId, data = {}) =>
  axios.post(`${API}/admin/customers/${userId}/onboarding/resend`, data, { headers: getHeaders() });

// Admin Agent CRUD
export const adminGetAgents = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/admin/agents${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const adminGetCustomerAgents = (userId) => 
  axios.get(`${API}/admin/customers/${userId}/agents`, { headers: getHeaders() });

export const adminGetAgent = (agentId) => 
  axios.get(`${API}/admin/agents/${agentId}`, { headers: getHeaders() });

export const adminCreateAgent = (data) => 
  axios.post(`${API}/admin/agents`, data, { headers: getHeaders() });

export const adminUpdateAgent = (agentId, data) => 
  axios.put(`${API}/admin/agents/${agentId}`, data, { headers: getHeaders() });

export const adminDeleteAgent = (agentId) => 
  axios.delete(`${API}/admin/agents/${agentId}`, { headers: getHeaders() });

export const adminAssignAgentSubscription = (agentId, planId) => 
  axios.post(`${API}/admin/agents/${agentId}/subscription`, { plan_id: planId }, { headers: getHeaders() });

/**
 * Admin: Stripe Customer Billing Portal for the subscription tied to this agent.
 * Backend ensures Stripe customer id, finds agent subscription with stripe_subscription_id, creates portal session.
 *
 * @param {string} agentId
 * @param {{ return_url?: string, send_email?: boolean }} [body] - send_email defaults true on server if omitted
 * @returns {Promise<import('axios').AxiosResponse<{ url?: string, portal_url?: string, billing_portal_url?: string }>>}
 */
export const adminCreateAgentStripeBillingPortalSession = (agentId, body = {}, config = {}) =>
  axios.post(
    `${API}/admin/agents/${encodeURIComponent(agentId)}/subscription/stripe-billing-portal`,
    body,
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

export const adminGetAgentConfiguration = (agentId) =>
  axios.get(`${API}/admin/agents/${agentId}/configuration`, { headers: getHeaders() });

export const adminUpsertAgentConfiguration = (agentId, data) =>
  axios.put(`${API}/admin/agents/${agentId}/configuration`, data, { headers: getHeaders() });

export const adminGenerateAgentConfiguration = (agentId, data = {}) =>
  axios.post(`${API}/admin/agents/${agentId}/configuration/generate`, data, { headers: getHeaders() });

export const adminAttachTwilioNumber = (agentId, data) =>
  axios.post(`${API}/admin/agents/${agentId}/twilio/attach`, data, { headers: getHeaders() });

export const adminDetachTwilioNumber = (agentId) =>
  axios.delete(`${API}/admin/agents/${agentId}/twilio/attach`, { headers: getHeaders() });

export const patchAdminAgentFacebookEnabled = (agentId, data, config = {}) =>
  axios.patch(`${API}/admin/agents/${encodeURIComponent(agentId)}/facebook-enabled`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

/** Admin: raw agent_subscription rows — list */
export const adminListAgentSubscriptions = (params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/admin/agent-subscriptions${query ? `?${query}` : ''}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });
};

export const adminGetAgentSubscription = (subscriptionId, config = {}) =>
  axios.get(`${API}/admin/agent-subscriptions/${encodeURIComponent(subscriptionId)}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const adminPatchAgentSubscription = (subscriptionId, data, config = {}) =>
  axios.patch(`${API}/admin/agent-subscriptions/${encodeURIComponent(subscriptionId)}`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const adminGetIndustries = () => 
  axios.get(`${API}/admin/industries`, { headers: getHeaders() });

// Password Reset
export const requestPasswordReset = (email) => 
  axios.post(`${API}/password/request`, { email });

export const verifyResetToken = (token) => 
  axios.get(`${API}/password/verify/${token}`);

export const resetPassword = (token, password) => 
  axios.post(`${API}/password/reset`, { token, password });

// Alert Preferences
export const getAlertPreferences = () => 
  axios.get(`${API}/alerts/preferences`, { headers: getHeaders() });

export const updateAlertPreferences = (data) => 
  axios.put(`${API}/alerts/preferences`, data, { headers: getHeaders() });

export const getAlertHistory = (limit = 20) => 
  axios.get(`${API}/alerts/history?limit=${limit}`, { headers: getHeaders() });

export const testSendAlert = (type) => 
  axios.post(`${API}/alerts/test`, { type }, { headers: getHeaders() });

// Business Customers (CRM)
export const getBusinessCustomers = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/customers${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getBusinessCustomer = (customerId) => 
  axios.get(`${API}/customers/${customerId}`, { headers: getHeaders() });

export const createBusinessCustomer = (data) => 
  axios.post(`${API}/customers`, data, { headers: getHeaders() });

export const updateBusinessCustomer = (customerId, data) => 
  axios.put(`${API}/customers/${customerId}`, data, { headers: getHeaders() });

export const deleteBusinessCustomer = (customerId) => 
  axios.delete(`${API}/customers/${customerId}`, { headers: getHeaders() });

export const convertCallToCustomer = (callId, data) => 
  axios.post(`${API}/customers/from-call/${callId}`, data, { headers: getHeaders() });

// Jobs
export const getJobs = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/jobs${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getJob = (jobId) => 
  axios.get(`${API}/jobs/${jobId}`, { headers: getHeaders() });

export const createJob = (data) => 
  axios.post(`${API}/jobs`, data, { headers: getHeaders() });

export const updateJob = (jobId, data) => 
  axios.put(`${API}/jobs/${jobId}`, data, { headers: getHeaders() });

export const deleteJob = (jobId) => 
  axios.delete(`${API}/jobs/${jobId}`, { headers: getHeaders() });

export const updateJobStatus = (jobId, status, notes = null) => 
  axios.patch(`${API}/jobs/${jobId}/status`, { status, notes }, { headers: getHeaders() });

export const getJobStats = () => 
  axios.get(`${API}/jobs/stats`, { headers: getHeaders() });

// Leads (CRM)
export const getLeads = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/leads${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getLead = (leadId) => 
  axios.get(`${API}/leads/${leadId}`, { headers: getHeaders() });

export const createLead = (data) => 
  axios.post(`${API}/leads`, data, { headers: getHeaders() });

export const updateLead = (leadId, data) => 
  axios.put(`${API}/leads/${leadId}`, data, { headers: getHeaders() });

export const deleteLead = (leadId) => 
  axios.delete(`${API}/leads/${leadId}`, { headers: getHeaders() });

export const updateLeadStatus = (leadId, status, notes = null, follow_up_date = null) => 
  axios.patch(`${API}/leads/${leadId}/status`, { status, notes, follow_up_date }, { headers: getHeaders() });

export const convertLead = (leadId, data) => 
  axios.post(`${API}/leads/${leadId}/convert`, data, { headers: getHeaders() });

export const addLeadComment = (leadId, content) => 
  axios.post(`${API}/leads/${leadId}/comments`, { content }, { headers: getHeaders() });

export const deleteLeadComment = (leadId, commentId) => 
  axios.delete(`${API}/leads/${leadId}/comments/${commentId}`, { headers: getHeaders() });

export const getLeadStats = () => 
  axios.get(`${API}/leads/stats`, { headers: getHeaders() });

export const getSalesUsers = () => 
  axios.get(`${API}/leads/sales-users`, { headers: getHeaders() });

export const bulkCreateLeads = (leads, source = 'csv_upload') => 
  axios.post(`${API}/leads/bulk`, { leads, source }, { headers: getHeaders() });

// Public lead capture (no auth required)
// Optional `pricing_context` can be included (from homepage calculator) for sales qualification.
export const captureLead = (data) => 
  axios.post(`${API}/leads/capture`, data);

// Public cold lead capture (no auth required)
// Accepts at least one of { email, phone }, plus optional { name, details, source, pricing_context }.
export const captureColdLead = (data) =>
  axios.post(`${API}/leads/capture-cold`, data);

// Customer Onboarding (invited customer setup) — always skip stored JWT; auth is via invite `token` query/body.
export const getCustomerOnboardingSession = (token) =>
  axios.get(`${API}/customer-onboarding/session?token=${encodeURIComponent(token)}`, { skipAuth: true });

export const completeCustomerOnboardingStepOne = (data) =>
  axios.post(`${API}/customer-onboarding/step-one`, data, { skipAuth: true });

export const getCustomerOnboardingPlans = (token, onboardingAccessToken = null) =>
  axios.get(`${API}/customer-onboarding/plans?token=${encodeURIComponent(token)}`, {
    headers: onboardingAccessToken ? { Authorization: `Bearer ${onboardingAccessToken}` } : {},
    skipAuth: !onboardingAccessToken,
  });

export const createCustomerOnboardingCheckout = (data) =>
  axios.post(`${API}/customer-onboarding/checkout`, data, { skipAuth: true });

export const getCustomerOnboardingStatus = ({ token = null, session_id = null } = {}) =>
  axios.get(`${API}/customer-onboarding/status`, {
    params: {
      ...(token ? { token } : {}),
      ...(session_id ? { session_id } : {}),
      _t: Date.now(),
    },
    skipAuth: true,
  });

// ============ USER MANAGEMENT (Admin) ============
export const getUsers = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/admin/users${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getStaffUsers = () => 
  axios.get(`${API}/admin/users/staff`, { headers: getHeaders() });

export const getUserById = (userId) => 
  axios.get(`${API}/admin/users/${userId}`, { headers: getHeaders() });

export const createUser = (data) => 
  axios.post(`${API}/admin/users`, data, { headers: getHeaders() });

export const updateUser = (userId, data) => 
  axios.put(`${API}/admin/users/${userId}`, data, { headers: getHeaders() });

export const deleteUser = (userId) => 
  axios.delete(`${API}/admin/users/${userId}`, { headers: getHeaders() });

export const resetUserPassword = (userId, newPassword) => 
  axios.post(`${API}/admin/users/${userId}/reset-password`, { new_password: newPassword }, { headers: getHeaders() });

// ============ SERVICE OPERATIONS ============
export const getServiceBusinesses = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/service/businesses${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getServiceBusiness = (businessId) => 
  axios.get(`${API}/service/businesses/${businessId}`, { headers: getHeaders() });

export const createServiceBusiness = (data) => 
  axios.post(`${API}/service/businesses`, data, { headers: getHeaders() });

export const getServiceAgents = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/service/agents${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getServiceAgent = (agentId) => 
  axios.get(`${API}/service/agents/${agentId}`, { headers: getHeaders() });

export const createServiceAgent = (data) => 
  axios.post(`${API}/service/agents`, data, { headers: getHeaders() });

export const serviceGetAgentConfiguration = (agentId) =>
  axios.get(`${API}/service/agents/${agentId}/configuration`, { headers: getHeaders() });

export const serviceUpsertAgentConfiguration = (agentId, data) =>
  axios.put(`${API}/service/agents/${agentId}/configuration`, data, { headers: getHeaders() });

export const serviceGenerateAgentConfiguration = (agentId, data = {}) =>
  axios.post(`${API}/service/agents/${agentId}/configuration/generate`, data, { headers: getHeaders() });

export const serviceAttachTwilioNumber = (agentId, data) =>
  axios.post(`${API}/service/agents/${agentId}/twilio/attach`, data, { headers: getHeaders() });

export const serviceDetachTwilioNumber = (agentId) =>
  axios.delete(`${API}/service/agents/${agentId}/twilio/attach`, { headers: getHeaders() });

export const patchServiceAgentFacebookEnabled = (agentId, data, config = {}) =>
  axios.patch(`${API}/service/agents/${encodeURIComponent(agentId)}/facebook-enabled`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const upsertTwilioAccount = (data) =>
  axios.post(`${API}/twilio/accounts`, data, { headers: getHeaders() });

/** Query params e.g. { agent_id } — list or filter Twilio account mappings (backend-dependent). */
export const getTwilioAccounts = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/twilio/accounts${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const addPackageToBusiness = (businessId, data) => 
  axios.post(`${API}/service/businesses/${businessId}/packages`, data, { headers: getHeaders() });

export const assignSubscriptionToAgent = (agentId, planId) => 
  axios.post(`${API}/service/agents/${agentId}/subscription`, { plan_id: planId }, { headers: getHeaders() });

export const getServiceCalls = (params = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/service/calls${query ? `?${query}` : ''}`, { headers: getHeaders() });
};

export const getServiceCall = (callId) => 
  axios.get(`${API}/service/calls/${callId}`, { headers: getHeaders() });

export const getServicePlans = () => 
  axios.get(`${API}/service/plans`, { headers: getHeaders() });

export const getServicePackages = () => 
  axios.get(`${API}/service/packages`, { headers: getHeaders() });

// ============ AGENT SCHEDULING / CALENDAR (API v1) ============
// Matches backend: /api/v1/agents/me/... (session agent), slot rules in calendar TZ, capacity by overlap.
// Public paused reads: 403 + { code: "CALENDAR_PAUSED" } on availability/busy (empty payload).
// Public POST: optional Idempotency-Key header (duplicate → 200, reused: true).

const SCHEDULING_API = `${API}/v1`;

/** Normalized API error: public availability/busy may return 403 with code CALENDAR_PAUSED */
export const isCalendarPausedPublicError = (error) => {
  if (error?.status !== 403) return false;
  const data = error?.data;
  if (!data || typeof data !== 'object') return false;
  if (data.code === 'CALENDAR_PAUSED') return true;
  if (data.detail === 'CALENDAR_PAUSED') return true;
  if (typeof data.detail === 'object' && data.detail?.code === 'CALENDAR_PAUSED') return true;
  return false;
};

export const getAgentCalendar = (config = {}) =>
  axios.get(`${SCHEDULING_API}/agents/me/calendar`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const createAgentCalendar = (data, config = {}) =>
  axios.post(`${SCHEDULING_API}/agents/me/calendar`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const patchAgentCalendar = (data, config = {}) =>
  axios.patch(`${SCHEDULING_API}/agents/me/calendar`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const pauseAgentCalendar = (config = {}) =>
  axios.post(`${SCHEDULING_API}/agents/me/calendar/pause`, {}, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const resumeAgentCalendar = (config = {}) =>
  axios.post(`${SCHEDULING_API}/agents/me/calendar/resume`, {}, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const rotateAgentCalendarPublicToken = (config = {}) =>
  axios.post(`${SCHEDULING_API}/agents/me/calendar/rotate-public-token`, {}, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const getAgentCalendarAppointments = (params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(
    `${SCHEDULING_API}/agents/me/calendar/appointments${query ? `?${query}` : ''}`,
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );
};

export const createAgentCalendarAppointment = (data, config = {}) =>
  axios.post(`${SCHEDULING_API}/agents/me/calendar/appointments`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const patchAgentCalendarAppointment = (appointmentId, data, config = {}) =>
  axios.patch(
    `${SCHEDULING_API}/agents/me/calendar/appointments/${encodeURIComponent(appointmentId)}`,
    data,
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

export const cancelAgentCalendarAppointment = (appointmentId, config = {}) =>
  axios.delete(
    `${SCHEDULING_API}/agents/me/calendar/appointments/${encodeURIComponent(appointmentId)}`,
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

export const getPublicCalendar = (publicToken, config = {}) =>
  axios.get(`${SCHEDULING_API}/public/calendars/${encodeURIComponent(publicToken)}`, {
    ...config,
  });

export const getPublicCalendarAvailability = (publicToken, params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(
    `${SCHEDULING_API}/public/calendars/${encodeURIComponent(publicToken)}/availability${query ? `?${query}` : ''}`,
    { ...config }
  );
};

export const getPublicCalendarBusy = (publicToken, params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(
    `${SCHEDULING_API}/public/calendars/${encodeURIComponent(publicToken)}/busy${query ? `?${query}` : ''}`,
    { ...config }
  );
};

/**
 * @param {string} publicToken
 * @param {object} data
 * @param {object} [config] — pass headers['Idempotency-Key'] for safe retries (server dedupes)
 */
export const createPublicCalendarAppointment = (publicToken, data, config = {}) =>
  axios.post(`${SCHEDULING_API}/public/calendars/${encodeURIComponent(publicToken)}/appointments`, data, {
    ...config,
    headers: {
      ...(config.headers || {}),
    },
  });

/**
 * External calendar (Google / Microsoft) OAuth + sync — see docs/calendar-external-sync-api.md
 * @param {string} agentId
 */
const calendarIntegrationsBase = (agentId) =>
  `${SCHEDULING_API}/agents/${encodeURIComponent(agentId)}/calendar/integrations`;

export const getCalendarIntegrations = (agentId, config = {}) =>
  axios.get(`${calendarIntegrationsBase(agentId)}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

/** @param {string} agentId @param {{ return_url?: string }} [data] */
export const startCalendarOAuthGoogle = (agentId, data = {}, config = {}) =>
  axios.post(`${calendarIntegrationsBase(agentId)}/google/authorize`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

/** @param {string} agentId @param {{ return_url?: string }} [data] */
export const startCalendarOAuthMicrosoft = (agentId, data = {}, config = {}) =>
  axios.post(`${calendarIntegrationsBase(agentId)}/microsoft/authorize`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const disconnectCalendarIntegration = (agentId, provider, config = {}) =>
  axios.delete(`${calendarIntegrationsBase(agentId)}/${encodeURIComponent(provider)}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

/** Ask backend to run sync now (push/pull) using stored refresh tokens */
export const triggerCalendarIntegrationSync = (agentId, config = {}) =>
  axios.post(`${calendarIntegrationsBase(agentId)}/sync`, {}, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

/** Same as sync for a specific agent, resolved from the authenticated session (My appointments). */
export const triggerMyCalendarIntegrationSync = (config = {}) =>
  axios.post(`${SCHEDULING_API}/agents/me/calendar/integrations/sync`, {}, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

// --- Customer support tickets (see docs/support-tickets-api.md) ---
export const createSupportTicket = (data, config = {}) =>
  axios.post(
    `${API}/support/tickets`,
    { agent_id: data.agent_id, title: data.title, content: data.content },
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

export const getMySupportTickets = (params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/support/tickets${query ? `?${query}` : ''}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });
};

export const getMySupportTicket = (ticketId, config = {}) =>
  axios.get(`${API}/support/tickets/${encodeURIComponent(ticketId)}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const addSupportTicketMessage = (ticketId, data, config = {}) =>
  axios.post(
    `${API}/support/tickets/${encodeURIComponent(ticketId)}/messages`,
    { content: data.content },
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

// --- Admin support ---
export const getAdminSupportSummary = (params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/admin/support/summary${query ? `?${query}` : ''}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });
};

export const getAdminSupportTickets = (params = {}, config = {}) => {
  const query = toQueryString(params);
  return axios.get(`${API}/admin/support/tickets${query ? `?${query}` : ''}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });
};

export const getAdminSupportTicket = (ticketId, config = {}) =>
  axios.get(`${API}/admin/support/tickets/${encodeURIComponent(ticketId)}`, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const patchAdminSupportTicket = (ticketId, data, config = {}) =>
  axios.patch(`${API}/admin/support/tickets/${encodeURIComponent(ticketId)}`, data, {
    ...config,
    headers: { ...getHeaders(), ...(config.headers || {}) },
  });

export const addAdminSupportTicketMessage = (ticketId, data, config = {}) =>
  axios.post(
    `${API}/admin/support/tickets/${encodeURIComponent(ticketId)}/messages`,
    { content: data.content },
    { ...config, headers: { ...getHeaders(), ...(config.headers || {}) } }
  );

